// src/pages/PropertyMetadataPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Divider,
  Skeleton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Checkbox,
  FormControlLabel,
  Snackbar,
  Alert,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Layout from "../components/layout/Layout";
import { useBreadcrumb } from "../context/BreadcrumbContext";
import {
  getPropertyDetails,
  getPortalWideLpe1Section12Defaults,
  updatePropertyMetadata,
} from "../api/propertyApi";
import { getLPE1CheckboxSchema, getLPE1FormFieldSchema } from "../api/lpe1Api";
import { getApiErrorMessage } from "../utils/concurrency";

export default function PropertyMetadataPage() {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const { setCustomPath, clearCustomPath } = useBreadcrumb();
  const [property, setProperty] = useState(null);
  const [metadata, setMetadata] = useState({});
  const draftRef = useRef({});
  const [checkboxSchema, setCheckboxSchema] = useState([]);
  const [formFieldSchema, setFormFieldSchema] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [metadataVersion, setMetadataVersion] = useState(0);
  const [portalSection13Defaults, setPortalSection13Defaults] = useState({});
  const [propertyRowVersion, setPropertyRowVersion] = useState(1);
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        setLoading(true);
        const [propertyData, checkboxSchemaData, fieldsSchemaData, portalDefaultsData] = await Promise.all([
          getPropertyDetails(propertyId),
          getLPE1CheckboxSchema(),
          getLPE1FormFieldSchema(),
          getPortalWideLpe1Section12Defaults(),
        ]);
        if (!active) return;
        setProperty(propertyData);
        setPropertyRowVersion(propertyData?.row_version || 1);
        setMetadata(propertyData?.metadata || {});
        draftRef.current = propertyData?.metadata || {};
        setMetadataVersion((prev) => prev + 1);
        setCheckboxSchema(checkboxSchemaData || []);
        setFormFieldSchema(fieldsSchemaData || []);
        setPortalSection13Defaults(portalDefaultsData || {});
      } catch (err) {
        console.error("Failed to load property metadata:", err);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [propertyId]);

  useEffect(() => {
    setCustomPath([
      { label: "Properties", path: "/properties" },
      { label: property?.name || "Property", path: `/properties/${propertyId}/details` },
      { label: "Company Information", path: null },
    ]);
    return () => clearCustomPath();
  }, [setCustomPath, clearCustomPath, propertyId, property?.name]);

  const totalUnits =
    property?.units?.length ??
    property?.unit_count ??
    metadata.total_units ??
    "";


  const getDraftValue = (key) => draftRef.current?.[key] ?? "";
  const setDraftValue = (key, value) => {
    draftRef.current = {
      ...draftRef.current,
      [key]: value,
    };
  };

  const Check = ({ label, checked, onChange }) => (
    <FormControlLabel
      control={<Checkbox checked={checked} onChange={onChange} />}
      label={label}
    />
  );

  const textValue = (key, readOnly) => {
    if (readOnly && (key === "4.1" || key === "7.1")) {
      return totalUnits === "" ? "" : String(totalUnits);
    }
    return getDraftValue(key);
  };

  const formFieldSchemaByKey = useMemo(() => {
    const map = {};
    formFieldSchema.forEach((item) => {
      map[item.field_key] = item;
    });
    return map;
  }, [formFieldSchema]);

  const fieldLabelFor = (fieldKey) => formFieldSchemaByKey[fieldKey]?.question || fieldKey;

  const BufferedText = ({
    label,
    value: currentValue,
    onCommit,
    readOnly = false,
    visuallyLocked = false,
    resetKey,
  }) => {
    const [local, setLocal] = useState(currentValue ?? "");

    useEffect(() => {
      setLocal(currentValue ?? "");
    }, [currentValue, resetKey]);

    return (
      <TextField
        label={label}
        fullWidth
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={() => !readOnly && onCommit(local)}
        InputProps={{ readOnly }}
        sx={
          visuallyLocked
            ? {
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "action.hover",
                  cursor: "not-allowed",
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "divider",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "divider",
                  },
                },
                "& .MuiInputBase-input": {
                  cursor: "not-allowed",
                },
                "& .MuiInputLabel-root": {
                  color: "text.secondary",
                },
              }
            : undefined
        }
      />
    );
  };

  const AutoText = ({ fieldKey, readOnly: forceReadOnly = false }) => {
    const isReadOnly = forceReadOnly || fieldKey === "4.1" || fieldKey === "7.1";
    return (
      <BufferedText
        label={fieldLabelFor(fieldKey)}
        value={textValue(fieldKey, isReadOnly)}
        onCommit={(nextValue) => setDraftValue(fieldKey, nextValue)}
        readOnly={isReadOnly}
        resetKey={metadataVersion}
      />
    );
  };

  const checkboxConfigByKey = useMemo(() => {
    const map = {};
    checkboxSchema.forEach((item) => {
      map[item.key] = item;
    });
    return map;
  }, [checkboxSchema]);

  const CheckboxQuestion = ({ config }) => {
    const [selected, setSelected] = useState(getDraftValue(config.key));

    return (
      <Box key={config.key} sx={{ mb: 2 }}>
        <Typography variant="subtitle1">{config.question}</Typography>
        <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
          {config.options.map((option) => (
            <Check
              key={`${config.key}-${option}`}
              label={option}
              checked={selected === option}
              onChange={() => {
                const nextValue = selected === option ? "" : option;
                setSelected(nextValue);
                setDraftValue(config.key, nextValue);
              }}
            />
          ))}
        </Stack>
      </Box>
    );
  };

  const renderCheckboxQuestion = (config) => {
    if (!config) return null;
    return <CheckboxQuestion key={`${config.key}-${metadataVersion}`} config={config} />;
  };

  const renderCheckboxByKey = (key) => renderCheckboxQuestion(checkboxConfigByKey[key]);

  const Section1Content = () => (
    <Stack spacing={4}>
      <Box>
        <Typography variant="h6">1.1 Landlord</Typography>
        <Stack spacing={2} mt={1}>
          <AutoText fieldKey="1.1 Name" />
          <AutoText fieldKey="1.1 Address" />
          <AutoText fieldKey="1.1 Telephone" />
          <AutoText fieldKey="1.1 Email" />
          <AutoText fieldKey="1.1 SC" />
          <AutoText fieldKey="1.1 Account Name" />
          <AutoText fieldKey="1.1 Account number" />
          <AutoText fieldKey="1.1 Reference" />
          <AutoText fieldKey="1.1 Fee Type" />
        </Stack>
      </Box>

      <Box>
        <Typography variant="h6">1.2 Management Company</Typography>
        <Stack spacing={2} mt={1}>
          {[
            "1.2 Name",
            "1.2 Address",
            "1.2 Telephone",
            "1.2 Email",
            "1.2 SC",
            "1.2 Account Name",
            "1.2 Account number",
            "1.2 Reference",
            "1.2 Fee Type",
          ].map((fieldKey) => (
            <AutoText key={fieldKey} fieldKey={fieldKey} />
          ))}
        </Stack>
      </Box>

      <Box>
        <Typography variant="h6">1.3 Managing Agent</Typography>
        <Stack spacing={2} mt={1}>
          <BufferedText
            label={fieldLabelFor("1.3 Name")}
            value={portalSection13Defaults?.["1.3 Name"] ?? ""}
            onCommit={() => {}}
            readOnly
            visuallyLocked
            resetKey={metadataVersion}
          />
          <BufferedText
            label={fieldLabelFor("1.3 Address")}
            value={portalSection13Defaults?.["1.3 Address"] ?? ""}
            onCommit={() => {}}
            readOnly
            visuallyLocked
            resetKey={metadataVersion}
          />
          <BufferedText
            label={fieldLabelFor("1.3 Telephone")}
            value={portalSection13Defaults?.["1.3 Telephone"] ?? ""}
            onCommit={() => {}}
            readOnly
            visuallyLocked
            resetKey={metadataVersion}
          />
          <BufferedText
            label={fieldLabelFor("1.3 Email")}
            value={portalSection13Defaults?.["1.3 Email"] ?? ""}
            onCommit={() => {}}
            readOnly
            visuallyLocked
            resetKey={metadataVersion}
          />
          <Box>{renderCheckboxByKey("appointed_by")}</Box>
          <BufferedText
            label={fieldLabelFor("1.3 SC")}
            value={portalSection13Defaults?.["1.3 SC"] ?? ""}
            onCommit={() => {}}
            readOnly
            visuallyLocked
            resetKey={metadataVersion}
          />
          <BufferedText
            label={fieldLabelFor("1.3 Account Name")}
            value={portalSection13Defaults?.["1.3 Account Name"] ?? ""}
            onCommit={() => {}}
            readOnly
            visuallyLocked
            resetKey={metadataVersion}
          />
          <BufferedText
            label={fieldLabelFor("1.3 Account number")}
            value={portalSection13Defaults?.["1.3 Account number"] ?? ""}
            onCommit={() => {}}
            readOnly
            visuallyLocked
            resetKey={metadataVersion}
          />
          <BufferedText
            label={fieldLabelFor("1.3 Reference")}
            value={portalSection13Defaults?.["1.3 Reference"] ?? ""}
            onCommit={() => {}}
            readOnly
            visuallyLocked
            resetKey={metadataVersion}
          />
          <BufferedText
            label={fieldLabelFor("1.3 Fee Type")}
            value={portalSection13Defaults?.["1.3 Fee Type"] ?? ""}
            onCommit={() => {}}
            readOnly
            visuallyLocked
            resetKey={metadataVersion}
          />
        </Stack>
      </Box>

      <Box>
        <Typography variant="h6">1.4 Residents'/Tenants' Association</Typography>
        <Stack spacing={2} mt={1}>
          <AutoText fieldKey="1.4 Name" />
          <AutoText fieldKey="1.4 Address" />
          <AutoText fieldKey="1.4 Telephone" />
          <AutoText fieldKey="1.4 Email" />
          <AutoText fieldKey="1.4 SC" />
          <AutoText fieldKey="1.4 Account Name" />
          <AutoText fieldKey="1.4 Account number" />
          <AutoText fieldKey="1.4 Reference" />
          <AutoText fieldKey="1.4 Fee Type" />
        </Stack>
      </Box>

      <Box>
        <Typography variant="h6">1.5 Legal Representative of one of the above</Typography>
        <Stack spacing={2} mt={1}>
          <BufferedText
            label="Name"
            value={textValue("1.5 Name", false)}
            onCommit={(nextValue) => setDraftValue("1.5 Name", nextValue)}
            resetKey={metadataVersion}
          />
          <BufferedText
            label="Address"
            value={textValue("1.5 Address", false)}
            onCommit={(nextValue) => setDraftValue("1.5 Address", nextValue)}
            resetKey={metadataVersion}
          />
          <BufferedText
            label="Telephone"
            value={textValue("1.5 Telephone", false)}
            onCommit={(nextValue) => setDraftValue("1.5 Telephone", nextValue)}
            resetKey={metadataVersion}
          />
          <BufferedText
            label="Email"
            value={textValue("1.5 Email", false)}
            onCommit={(nextValue) => setDraftValue("1.5 Email", nextValue)}
            resetKey={metadataVersion}
          />
          <Box>{renderCheckboxByKey("1.5 appointed_by")}</Box>
        </Stack>
      </Box>

      <Box>
        <Typography variant="h6">Responsibilities</Typography>
        <Stack spacing={2} mt={1}>
          <Box>{renderCheckboxByKey("1.7")}</Box>
          <Box>{renderCheckboxByKey("1.8")}</Box>
          <Box>{renderCheckboxByKey("1.9")}</Box>
          <Box>{renderCheckboxByKey("1.10")}</Box>
        </Stack>
      </Box>

      <Box>
        <Typography variant="h6">
          1.6 Who accepts service of the Notice of Assignment &amp; Charge?
        </Typography>
        <Stack spacing={2} mt={1}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box>{renderCheckboxByKey("1.6_landlord")}</Box>
            <Box sx={{ flex: 1 }}>
              <AutoText fieldKey="1.6 Landlord £" />
            </Box>
            <Box>{renderCheckboxByKey("1.6_notice_landlord")}</Box>
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box>{renderCheckboxByKey("1.6_management_company")}</Box>
            <Box sx={{ flex: 1 }}>
              <AutoText fieldKey="1.6 MC £" />
            </Box>
            <Box>{renderCheckboxByKey("1.6_notice_management_company")}</Box>
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box>{renderCheckboxByKey("1.6_managing_agent")}</Box>
            <Box sx={{ flex: 1 }}>
              <AutoText fieldKey="1.6 MA £" />
            </Box>
            <Box>{renderCheckboxByKey("1.6_notice_managing_agent")}</Box>
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box>{renderCheckboxByKey("1.6_other")}</Box>
            <Box sx={{ flex: 1 }}>
              <AutoText fieldKey="1.6 Other £" />
            </Box>
            <Box>{renderCheckboxByKey("1.6_notice_other")}</Box>
          </Stack>
          <AutoText fieldKey="1.6 Name" />
          <AutoText fieldKey="1.6 Address" />
          <AutoText fieldKey="1.6 Telephone" />
          <AutoText fieldKey="1.6 Email" />
          <AutoText fieldKey="1.6 Capacity" />
        </Stack>
      </Box>

      <Box>
        <Typography variant="subtitle1">
          1.10.1 If the Lessees, please provide the contact details of the
          Lessee in charge
        </Typography>
        <Stack spacing={2} mt={1}>
          <AutoText fieldKey="1.10.1" />
          <Box>{renderCheckboxByKey("1.11")}</Box>
          <Box>{renderCheckboxByKey("1.12")}</Box>
        </Stack>
      </Box>
    </Stack>
  );

  const Section2Content = () => (
    <Stack spacing={3}>
      <Box>{renderCheckboxByKey("2.1")}</Box>
      <AutoText fieldKey="2.1.1 £" />
      <AutoText fieldKey="2.1.2" />
      <Box>{renderCheckboxByKey("2.2")}</Box>
      <AutoText fieldKey="2.3" />
      <Box>{renderCheckboxByKey("2.4")}</Box>
      <AutoText fieldKey="2.4.1" />
      <Box>{renderCheckboxByKey("2.5")}</Box>
      <AutoText fieldKey="2.5.1" />
      <AutoText fieldKey="2.6" />
    </Stack>
  );

  const Section3Content = () => (
    <Stack spacing={3}>
      <AutoText fieldKey="3.1 £" />
      <Box>{renderCheckboxByKey("3.2")}</Box>
      <AutoText fieldKey="3.2.1" />
      <Typography variant="subtitle1">3.3 What period is covered by the last demand?</Typography>
      <Stack direction="row" spacing={2}>
        <AutoText fieldKey="3.3 From DD" />
        <AutoText fieldKey="3.3 From MM" />
        <AutoText fieldKey="3.3 From YYYY" />
      </Stack>
      <Stack direction="row" spacing={2}>
        <AutoText fieldKey="3.3 To DD" />
        <AutoText fieldKey="3.3 To MM" />
        <AutoText fieldKey="3.3 To YYYY" />
      </Stack>
    </Stack>
  );

  const Section4Content = () => (
    <Stack spacing={3}>
      <AutoText fieldKey="4.1" />
      <AutoText fieldKey="411 £" />
      <AutoText fieldKey="412" />
      <Box>{renderCheckboxByKey("4.2")}</Box>
      <AutoText fieldKey="4.2.1" />
      <Box>{renderCheckboxByKey("4.3")}</Box>
      <AutoText fieldKey="4.3.1" />
      <Typography variant="subtitle1">4.4 What period is covered by the last demand?</Typography>
      <Stack direction="row" spacing={2}>
        <AutoText fieldKey="4.4 From DD" />
        <AutoText fieldKey="4.4 From MM" />
        <AutoText fieldKey="4.4 From YYYY" />
      </Stack>
      <Stack direction="row" spacing={2}>
        <AutoText fieldKey="4.4 To DD" />
        <AutoText fieldKey="4.4 To MM" />
        <AutoText fieldKey="4.4 To YYYY" />
      </Stack>
      <Box>{renderCheckboxByKey("4.5")}</Box>
      <AutoText fieldKey="4.5.1" />
      <Box>{renderCheckboxByKey("4.6")}</Box>
      <AutoText fieldKey="4.6.1 £" />
      <Box>{renderCheckboxByKey("4.6.2")}</Box>
      <AutoText fieldKey="4.6.3" />
      <Typography variant="subtitle1">4.7 Date when Managed Areas were last decorated</Typography>
      <Stack direction="row" spacing={2}>
        <AutoText fieldKey="4.7 ID DD" />
        <AutoText fieldKey="4.7 ID MM" />
        <AutoText fieldKey="4.7 ID YYYY" />
      </Stack>
      <Stack direction="row" spacing={2}>
        <AutoText fieldKey="4.7 ID TO DD" />
        <AutoText fieldKey="4.7 ID TO MM" />
        <AutoText fieldKey="4.7 ID TO YYYY" />
      </Stack>
      <Stack direction="row" spacing={2}>
        <AutoText fieldKey="4.7 ED DD" />
        <AutoText fieldKey="4.7 ED MM" />
        <AutoText fieldKey="4.7 ED YYYY" />
      </Stack>
      <Stack direction="row" spacing={2}>
        <AutoText fieldKey="4.7 ED TO DD" />
        <AutoText fieldKey="4.7 ED TO MM" />
        <AutoText fieldKey="4.7 ED TO YYYY" />
      </Stack>
      <Box>{renderCheckboxByKey("4.8")}</Box>
      <AutoText fieldKey="4.8.1" />
      <Box>{renderCheckboxByKey("4.9")}</Box>
      <AutoText fieldKey="4.9.1" />
      <Box>{renderCheckboxByKey("4.10")}</Box>
      <AutoText fieldKey="4.10.1" />
      <Box>{renderCheckboxByKey("4.11")}</Box>
      <AutoText fieldKey="4.11.1" />
      <Box>{renderCheckboxByKey("4.12")}</Box>
      <AutoText fieldKey="4.12.1" />
      <Box>{renderCheckboxByKey("4.13")}</Box>
      <AutoText fieldKey="4.13.1" />
    </Stack>
  );

  const Section5Content = () => (
    <Stack spacing={3}>
      <Box>{renderCheckboxByKey("5.1")}</Box>
      <AutoText fieldKey="5.1.1" />
      <Typography variant="subtitle1">5.2 What period is covered by the last demand?</Typography>
      <Stack direction="row" spacing={2}>
        <AutoText fieldKey="5.2 From DD" />
        <AutoText fieldKey="5.2 From MM" />
        <AutoText fieldKey="5.2 From YYYY" />
      </Stack>
      <Stack direction="row" spacing={2}>
        <AutoText fieldKey="5.2 To DD" />
        <AutoText fieldKey="5.2 To MM" />
        <AutoText fieldKey="5.2 To YYYY" />
      </Stack>
      <Box>{renderCheckboxByKey("5.3")}</Box>
      <AutoText fieldKey="5.3.1" />
      <Box>{renderCheckboxByKey("5.4")}</Box>
      <AutoText fieldKey="5.4.1" />
      <Box>{renderCheckboxByKey("5.5")}</Box>
      <AutoText fieldKey="5.5.1" />
      <Box>{renderCheckboxByKey("5.6")}</Box>
      <Box>{renderCheckboxByKey("5.6.1i")}</Box>
      <Box>{renderCheckboxByKey("5.6.1ii")}</Box>
      <Box>{renderCheckboxByKey("5.6.2i")}</Box>
      <Box>{renderCheckboxByKey("5.6.2ii")}</Box>
      <Box>{renderCheckboxByKey("5.7")}</Box>
      <AutoText fieldKey="5.7.1" />
      <Box>{renderCheckboxByKey("5.8")}</Box>
      <AutoText fieldKey="5.8.1" />
      <Typography variant="subtitle1">5.9 Date of last buildings reinstatement cost assessment</Typography>
      <Stack direction="row" spacing={2}>
        <AutoText fieldKey="5.9 DD" />
        <AutoText fieldKey="5.9 MM" />
        <AutoText fieldKey="5.9 YYYY" />
      </Stack>
      <Box>{renderCheckboxByKey("5.10")}</Box>
      <AutoText fieldKey="5.10.1 £" />
    </Stack>
  );

  const Section6Content = () => (
    <Stack spacing={3}>
      <Box>{renderCheckboxByKey("6.1")}</Box>
      <Box>{renderCheckboxByKey("6.2")}</Box>
      <AutoText fieldKey="6.2.1" />
      <Box>{renderCheckboxByKey("6.3")}</Box>
      <AutoText fieldKey="6.3.1" />
      <Box>{renderCheckboxByKey("6.4")}</Box>
      <AutoText fieldKey="6.4.1" />
    </Stack>
  );

  const Section7Content = () => (
    <Stack spacing={3}>
      <AutoText fieldKey="7.1" />
      <Box>{renderCheckboxByKey("7.2")}</Box>
      <AutoText fieldKey="7.2.1" />
      <Box>{renderCheckboxByKey("7.3")}</Box>
      <AutoText fieldKey="7.3.1" />
      <Box>{renderCheckboxByKey("7.4")}</Box>
      <AutoText fieldKey="7.4.1" />
      <Box>{renderCheckboxByKey("7.5_restriction")}</Box>
      <AutoText fieldKey="7.5" />
    </Stack>
  );

  const Section8Content = () => (
    <Stack spacing={3}>
      <Box>{renderCheckboxByKey("8.1")}</Box>
      <Box>{renderCheckboxByKey("8.2")}</Box>
      <Box>{renderCheckboxByKey("8.3")}</Box>
      <Box>{renderCheckboxByKey("8.4")}</Box>
      <Box>{renderCheckboxByKey("8.5")}</Box>
      <Box>{renderCheckboxByKey("8.6")}</Box>
      <Box>{renderCheckboxByKey("8.7")}</Box>
      <Box>{renderCheckboxByKey("8.8")}</Box>
      <Box>{renderCheckboxByKey("8.9")}</Box>
      <Box>{renderCheckboxByKey("8.10")}</Box>
      <Box>{renderCheckboxByKey("8.11")}</Box>
      <AutoText fieldKey="8.11 Fee" />
      <Box>{renderCheckboxByKey("8.11_sub")}</Box>
      <Box>{renderCheckboxByKey("8.12")}</Box>
      <Box>{renderCheckboxByKey("8.13")}</Box>
      <Box>{renderCheckboxByKey("8.14")}</Box>
      <Box>{renderCheckboxByKey("8.15")}</Box>
      <Box>{renderCheckboxByKey("8.16")}</Box>
      <Box>{renderCheckboxByKey("8.17")}</Box>
      <Box>{renderCheckboxByKey("8.18")}</Box>
    </Stack>
  );

  const renderSectionContent = (section) => {
    switch (section) {
      case 1:
        return <Section1Content />;
      case 2:
        return <Section2Content />;
      case 3:
        return <Section3Content />;
      case 4:
        return <Section4Content />;
      case 5:
        return <Section5Content />;
      case 6:
        return <Section6Content />;
      case 7:
        return <Section7Content />;
      case 8:
        return <Section8Content />;
      default:
        return null;
    }
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <Box sx={{ mb: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="h5" fontWeight={600}>
            LPE1 Metadata
          </Typography>
          <Button variant="text" onClick={() => navigate(-1)}>
            Back
          </Button>
        </Box>

        {loading ? (
          <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
            <Skeleton variant="text" width="40%" />
            <Skeleton variant="rectangular" height={200} sx={{ mt: 2 }} />
          </Paper>
        ) : (
          <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
            {property && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {property.name || "Property"} • {property.address_line1}
                {property.address_line2 ? `, ${property.address_line2}` : ""}
              </Typography>
            )}
            {[1, 2, 3, 4, 5, 6, 7, 8].map((section) => {
              const content = renderSectionContent(section);
              if (!content) return null;
              return (
                <Accordion key={`section-${section}`} defaultExpanded={false}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      Section {section}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>{content}</AccordionDetails>
                </Accordion>
              );
            })}

            <Divider sx={{ my: 2 }} />

            <Button
              variant="contained"
              disabled={saving}
              onClick={async () => {
                try {
                  setSaving(true);
                  const response = await updatePropertyMetadata(propertyId, {
                    ...draftRef.current,
                    total_units: totalUnits === "" ? metadata.total_units : Number(totalUnits),
                    row_version: propertyRowVersion,
                  });
                  setMetadata(draftRef.current);
                  if (response?.data?.row_version) {
                    setPropertyRowVersion(response.data.row_version);
                  }
                  setToast({
                    open: true,
                    message: "Metadata updated",
                    severity: "success",
                  });
                } catch (err) {
                  console.error("Failed to save LPE1 metadata:", err);
                  setToast({
                    open: true,
                    message: getApiErrorMessage(err, "Failed to update metadata"),
                    severity: "error",
                  });
                } finally {
                  setSaving(false);
                }
              }}
            >
              Save Information
            </Button>
          </Paper>
        )}
      </Box>

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity={toast.severity} variant="filled" onClose={() => setToast((prev) => ({ ...prev, open: false }))}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
}
